export type DiagnosticSeverity = "INFO" | "WARNING" | "ERROR" | "CRITICAL";

export type DiagnosticCategory =
  | "USER_ACTION"
  | "NETWORK"
  | "AUTH"
  | "UI"
  | "SYSTEM";

export type DiagnosticOutcome =
  | "started"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "deduplicated"
  | "offline"
  | "online"
  | "unknown";

/* Event names describe an intent or an outcome, never a physical tap. Keeping the vocabulary
   here prevents individual screens from inventing subtly different names for the same action. */
export type DiagnosticEventName =
  | "app.lifecycle"
  | "app.error"
  | "auth.session"
  | "navigation.screen"
  | "table.view"
  | "draft.changed"
  | "order.submit"
  | "order.print"
  | "order.mutation"
  | "table.mutation"
  | "history.mutation"
  | "setup.patch"
  | "account.password_change"
  | "printer.check"
  | "connection.state"
  | "sync.cycle"
  | "network.request";

export type DiagnosticEntityType =
  | "order"
  | "order_item"
  | "product"
  | "table"
  | "printer"
  | "setup";

const DIAGNOSTIC_ENTITY_TYPES = new Set<DiagnosticEntityType>([
  "order",
  "order_item",
  "product",
  "table",
  "printer",
  "setup"
]);

const SAFE_DIAGNOSTIC_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/;
const SAFE_DIAGNOSTIC_ENTITY_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

const DIAGNOSTIC_SEVERITIES = new Set<DiagnosticSeverity>([
  "INFO",
  "WARNING",
  "ERROR",
  "CRITICAL"
]);
const DIAGNOSTIC_CATEGORIES = new Set<DiagnosticCategory>([
  "USER_ACTION",
  "NETWORK",
  "AUTH",
  "UI",
  "SYSTEM"
]);
const DIAGNOSTIC_EVENT_NAMES = new Set<DiagnosticEventName>([
  "app.lifecycle",
  "app.error",
  "auth.session",
  "navigation.screen",
  "table.view",
  "draft.changed",
  "order.submit",
  "order.print",
  "order.mutation",
  "table.mutation",
  "history.mutation",
  "setup.patch",
  "account.password_change",
  "printer.check",
  "connection.state",
  "sync.cycle",
  "network.request"
]);
const DIAGNOSTIC_OUTCOMES = new Set<DiagnosticOutcome>([
  "started",
  "succeeded",
  "failed",
  "cancelled",
  "deduplicated",
  "offline",
  "online",
  "unknown"
]);
const BROWSER_FAMILIES = new Set(["chrome", "chromium", "edge", "firefox", "safari", "other"]);
const OS_FAMILIES = new Set(["ios", "android", "windows", "macos", "linux", "other"]);

export type DiagnosticRuntimeContext = {
  browser_family: string;
  os_family: string;
  display_mode: "standalone" | "browser";
  online: boolean;
};

export type StoredDiagnosticEvent = {
  event_id: string;
  occurred_at: number;
  diagnostic_session_id: string;
  sequence: number;
  severity: DiagnosticSeverity;
  category: DiagnosticCategory;
  event_name: DiagnosticEventName;
  outcome?: DiagnosticOutcome;
  action_id?: string;
  request_id?: string;
  entity_type?: DiagnosticEntityType;
  entity_id?: string;
  platform: "pwa";
  app_version: string;
  app_version_code: number;
  build_id: string;
  context: DiagnosticRuntimeContext;
  metadata: DiagnosticMetadata;
};

export type StoredDiagnosticNormalizationOptions = {
  diagnosticSessionId: string;
  cutoff: number;
  appVersion: string;
  appVersionCode: number;
  buildId: string;
  fallbackContext: DiagnosticRuntimeContext;
};

export type StoredDiagnosticNormalizationResult = {
  events: StoredDiagnosticEvent[];
  /** IndexedDB keys that are safe to delete locally but must never be transmitted. */
  discardedEventIds: string[];
};

export const DIAGNOSTIC_METADATA_KEYS = [
  "attempt",
  "changed_count",
  "connection",
  "column",
  "deduplicated",
  "delete_count",
  "duration_ms",
  "error_code",
  "error_kind",
  "forced",
  "fingerprint",
  "item_count",
  "line_id",
  "line",
  "merge_id",
  "method",
  "online",
  "operation",
  "order_id",
  "page_id",
  "pending_state",
  "permission",
  "poll",
  "previous_connection",
  "previous_quantity",
  "previous_screen",
  "print_job_id",
  "printer_count",
  "product_id",
  "profile",
  "quantity",
  "queue_size",
  "reason",
  "replaced_session",
  "result",
  "retry",
  "route",
  "screen",
  "script",
  "served",
  "setup_collection",
  "source",
  "state",
  "status",
  "submission_id",
  "table_id",
  "target_quantity",
  "upsert_count"
] as const;

export type DiagnosticMetadataKey = (typeof DIAGNOSTIC_METADATA_KEYS)[number];
export type DiagnosticMetadataValue = string | number | boolean | null;
export type DiagnosticMetadataInput = Partial<Record<DiagnosticMetadataKey, unknown>>;
export type DiagnosticMetadata = Partial<Record<DiagnosticMetadataKey, DiagnosticMetadataValue>>;

const ALLOWED_KEYS = new Set<string>(DIAGNOSTIC_METADATA_KEYS);
const MAX_STRING_LENGTH = 120;
const SAFE_STRING = /^[a-zA-Z0-9_./:-]+$/;

/* These fields must never make it into diagnostics even if a future caller defeats the
   TypeScript type with a cast. Values are also constrained to machine-oriented identifiers and
   enums: prose belongs in the UI, not in the telemetry store. */
const SENSITIVE_KEY = /(password|token|secret|authorization|cookie|comment|note|name|label|search|query|body|payload|image|email|phone|address|user_agent|stack|message)/i;

/** Correlation/session IDs cross both the persistence and HTTP privacy boundaries. Keep this
    runtime check even though callers are typed: browser callbacks can supply event objects and
    persisted data can pre-date the current TypeScript definitions. */
export function sanitizeDiagnosticId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const clean = value.trim();
  return SAFE_DIAGNOSTIC_ID.test(clean) ? clean : undefined;
}

export function sanitizeDiagnosticEntityType(value: unknown): DiagnosticEntityType | undefined {
  return typeof value === "string" && DIAGNOSTIC_ENTITY_TYPES.has(value as DiagnosticEntityType)
    ? value as DiagnosticEntityType
    : undefined;
}

export function sanitizeDiagnosticEntityId(value: unknown): string | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  if (typeof value === "number" && (!Number.isSafeInteger(value) || value < 0)) return undefined;
  const clean = String(value).trim();
  return SAFE_DIAGNOSTIC_ENTITY_ID.test(clean) ? clean : undefined;
}

export function sanitizeDiagnosticMetadata(input?: Record<string, unknown>): DiagnosticMetadata {
  const clean: DiagnosticMetadata = {};
  if (!input) {
    return clean;
  }
  for (const [key, raw] of Object.entries(input)) {
    if (!ALLOWED_KEYS.has(key) || SENSITIVE_KEY.test(key)) {
      continue;
    }
    if (raw === null || typeof raw === "boolean") {
      clean[key as DiagnosticMetadataKey] = raw;
      continue;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      clean[key as DiagnosticMetadataKey] = Math.max(-1_000_000_000, Math.min(1_000_000_000, raw));
      continue;
    }
    if (typeof raw === "string" && raw.length <= MAX_STRING_LENGTH && SAFE_STRING.test(raw)) {
      clean[key as DiagnosticMetadataKey] = raw;
    }
  }
  return clean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function safeMachineString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length <= MAX_STRING_LENGTH && SAFE_STRING.test(value)
    ? value
    : fallback;
}

function normalizeStoredDiagnosticEvent(
  input: unknown,
  options: StoredDiagnosticNormalizationOptions
): StoredDiagnosticEvent | undefined {
  try {
    if (!isRecord(input)) return undefined;
    const eventId = sanitizeDiagnosticId(input.event_id);
    const diagnosticSessionId = sanitizeDiagnosticId(input.diagnostic_session_id);
    const occurredAt = input.occurred_at;
    const sequence = input.sequence;
    if (
      !eventId ||
      diagnosticSessionId !== options.diagnosticSessionId ||
      typeof occurredAt !== "number" ||
      !Number.isFinite(occurredAt) ||
      occurredAt < options.cutoff ||
      typeof sequence !== "number" ||
      !Number.isSafeInteger(sequence) ||
      sequence < 1 ||
      typeof input.severity !== "string" ||
      !DIAGNOSTIC_SEVERITIES.has(input.severity as DiagnosticSeverity) ||
      typeof input.category !== "string" ||
      !DIAGNOSTIC_CATEGORIES.has(input.category as DiagnosticCategory) ||
      typeof input.event_name !== "string" ||
      !DIAGNOSTIC_EVENT_NAMES.has(input.event_name as DiagnosticEventName)
    ) {
      return undefined;
    }

    const rawContext = isRecord(input.context) ? input.context : {};
    const fallbackContext = options.fallbackContext;
    const browserFamily = typeof rawContext.browser_family === "string" &&
      BROWSER_FAMILIES.has(rawContext.browser_family)
      ? rawContext.browser_family
      : fallbackContext.browser_family;
    const osFamily = typeof rawContext.os_family === "string" && OS_FAMILIES.has(rawContext.os_family)
      ? rawContext.os_family
      : fallbackContext.os_family;
    const display = rawContext.display_mode === "standalone" || rawContext.display_mode === "browser"
      ? rawContext.display_mode
      : fallbackContext.display_mode;
    const online = typeof rawContext.online === "boolean"
      ? rawContext.online
      : fallbackContext.online;
    const outcome = typeof input.outcome === "string" &&
      DIAGNOSTIC_OUTCOMES.has(input.outcome as DiagnosticOutcome)
      ? input.outcome as DiagnosticOutcome
      : undefined;
    const actionId = sanitizeDiagnosticId(input.action_id);
    const requestId = sanitizeDiagnosticId(input.request_id);
    const entityType = sanitizeDiagnosticEntityType(input.entity_type);
    const entityId = entityType ? sanitizeDiagnosticEntityId(input.entity_id) : undefined;

    /* Rebuild, rather than mutate, the persisted row. IndexedDB structured clone supports cycles;
       selecting only validated scalar fields guarantees the outbound object is JSON-safe even if a
       pre-upgrade row contains a cyclic callback object in action_id, metadata, or an unknown key. */
    return {
      event_id: eventId,
      occurred_at: occurredAt,
      diagnostic_session_id: diagnosticSessionId,
      sequence,
      severity: input.severity as DiagnosticSeverity,
      category: input.category as DiagnosticCategory,
      event_name: input.event_name as DiagnosticEventName,
      outcome,
      action_id: actionId,
      request_id: requestId,
      entity_type: entityId === undefined ? undefined : entityType,
      entity_id: entityId,
      platform: "pwa",
      app_version: safeMachineString(input.app_version, options.appVersion),
      app_version_code: typeof input.app_version_code === "number" &&
        Number.isSafeInteger(input.app_version_code) && input.app_version_code >= 0
        ? input.app_version_code
        : options.appVersionCode,
      build_id: safeMachineString(input.build_id, options.buildId),
      context: {
        browser_family: browserFamily,
        os_family: osFamily,
        display_mode: display,
        online
      },
      metadata: sanitizeDiagnosticMetadata(isRecord(input.metadata) ? input.metadata : undefined)
    };
  } catch {
    // A malformed persisted record is diagnostics data, never an application failure.
    return undefined;
  }
}

/**
 * Re-normalizes every persisted row independently. One malformed/cyclic row is discarded while
 * later valid events continue through the queue. Raw records are never returned to the transport.
 */
export function normalizeStoredDiagnosticEvents(
  records: readonly unknown[],
  options: StoredDiagnosticNormalizationOptions
): StoredDiagnosticNormalizationResult {
  const events: StoredDiagnosticEvent[] = [];
  const discardedEventIds: string[] = [];
  for (const record of records) {
    const event = normalizeStoredDiagnosticEvent(record, options);
    if (event) {
      events.push(event);
      continue;
    }
    try {
      if (isRecord(record) && typeof record.event_id === "string") {
        discardedEventIds.push(record.event_id);
      }
    } catch {
      // An exotic object with throwing accessors cannot come from IndexedDB, but is still ignored.
    }
  }
  return { events, discardedEventIds };
}

export function safeDiagnosticError(error: unknown): {
  error_kind: string;
  error_code: string;
} {
  if (typeof error === "object" && error !== null) {
    const candidate = error as { name?: unknown; code?: unknown };
    const kind = typeof candidate.name === "string" && SAFE_STRING.test(candidate.name)
      ? candidate.name.slice(0, MAX_STRING_LENGTH)
      : "Error";
    const code = typeof candidate.code === "string" && SAFE_STRING.test(candidate.code)
      ? candidate.code.slice(0, MAX_STRING_LENGTH)
      : "operation_failed";
    return { error_kind: kind, error_code: code };
  }
  return { error_kind: typeof error, error_code: "operation_failed" };
}
